/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as React from 'react';
import { VictoryArea, VictoryChart, VictoryAxis, VictoryLabel } from 'victory';
import { format } from 'date-fns';

import { styledEstimatesGraphStyle } from './PreviewGraph.styles.css';

import {
    createTickLabels,
    createDataPoints,
    shortenAndFormatPrice,
    createMinDomain,
} from '../utils/utils';
import { previewGraphTheme } from '../Estimates.styles';

const NUM_MONTHS = 25; // Includes the current month from 2 years ago
const NUM_LABELS = 4; // Graph will automatically choose closest number that can downsample evenly

interface SaleEstimate {
    builtAt?: string | null;
    currentPrice: number;
}

export type PreviewGraphProps = {
    saleEstimates: SaleEstimate[];
    obscure?: boolean;
    isModalOpen: boolean;
    onClick: () => void;
};

const PreviewGraph: React.FC<React.PropsWithChildren<PreviewGraphProps>> = ({
    saleEstimates,
    obscure,
    onClick,
    isModalOpen,
}) => {
    if (!saleEstimates || saleEstimates.length <= 1) return null;

    // These helpers allow us to have the control we need over the number of labels
    // And data points that we want to display
    const finalTickLabel = saleEstimates[saleEstimates.length - 1].builtAt;
    if (!finalTickLabel) return null;
    const tickLabels = createTickLabels(NUM_MONTHS, NUM_LABELS, finalTickLabel);
    const dataPoints = createDataPoints(saleEstimates, tickLabels);
    const minDomain = { y: createMinDomain(dataPoints) };

    return (
        <div
            className={styledEstimatesGraphStyle}
            aria-label="Estimate graph. See inside the estimate"
            data-testid="preview-estimate-graph"
            role="button"
            tabIndex={0}
            aria-pressed={isModalOpen}
            onClick={() => !isModalOpen && onClick()}
        >
            <div aria-hidden>
                <VictoryChart
                    theme={previewGraphTheme(obscure)}
                    minDomain={minDomain}
                >
                    <VictoryAxis
                        fixLabelOverlap={true}
                        tickValues={tickLabels}
                        tickFormat={(label) =>
                            format(new Date(label), 'MMM yy')
                        }
                        tickLabelComponent={
                            <VictoryLabel
                                renderInPortal={true}
                                // @ts-ignore Due to a bug in Victory types
                                textAnchor={({ index }) => {
                                    if (index === 0) {
                                        return 'start';
                                    }
                                    if (index === NUM_LABELS - 1) {
                                        return 'end';
                                    }
                                    return 'middle';
                                }}
                            />
                        }
                    />
                    <VictoryAxis
                        dependentAxis
                        tickFormat={shortenAndFormatPrice}
                    />
                    <VictoryArea
                        data={dataPoints}
                        x="builtAt"
                        y="currentPrice"
                    />
                </VictoryChart>
            </div>
        </div>
    );
};

export default PreviewGraph;
